import { FC, useCallback, useMemo } from "react";

import { KpkApiFormType } from "@keepeek/api-client";
import { formsQuerySelector, FunctionOverrideKey, useCurrentUser } from "@keepeek/commons";
import {
  ClassificationDestination,
  ClassificationDestinationOptionsFolder,
  ClassificationDestinationType,
  KFImport,
  UploadKey,
} from "@keepeek/refront-components";
import { useRecoilState, useRecoilValueLoadable } from "recoil";

import { KIcon } from "../../components/common/KIcon";
import { CONFIGURATION_SECTION_PATH } from "../../components/layout/adminMenu";
import { getCustomerOverrideFunction } from "../../lib/overrides";
import {
  Import,
  Type,
  ClassificationDestination as ClassificationDestinationConf,
} from "../../models/configuration/components/import";
import { ImportClassificationDestinationFolderOption } from "../../models/configuration/definitions/importClassificationDestinationFolderOption";
import { useConfiguration } from "../../providers/config/hooks";
import { importModalOpenState } from "../../providers/import/atoms";

const ImportElement: FC<React.PropsWithChildren<unknown>> = function () {
  const [importModalOpen, setImportModalOpen] = useRecoilState(importModalOpenState);
  const formsQuery = useRecoilValueLoadable(formsQuerySelector);
  const forms = formsQuery.state === "hasValue" ? formsQuery.contents : null;
  const {
    fields,
    formsMapping,
    indexInAllAvailableLocale,
    classificationDestination: classificationDestinationConf,
  } = useConfiguration<Import>(CONFIGURATION_SECTION_PATH.COMPONENTS_IMPORT) ?? {
    fields: undefined,
    formsMapping: undefined,
    indexInAllAvailableLocale: undefined,
    classificationDestination: undefined,
  };
  const { currentUser } = useCurrentUser();
  const handleClose = useCallback(() => setImportModalOpen(false), [setImportModalOpen]);
  const internalFieldNames = useMemo(
    () =>
      fields?.map((f) => {
        return {
          field: f.field || "",
          required: f.required,
          thesaurusSettings: f.thesaurusSettings,
        };
      }) || [],
    [fields],
  );
  const formTypeFormIdMappings = useMemo(
    () =>
      formsMapping?.map((fm) => {
        return {
          formId: fm.formId,
          formType: forms?.find((f) => f.id === fm.formId)?.type || KpkApiFormType.Picture,
        };
      }),
    [forms, formsMapping],
  );
  const classificationDestination: ClassificationDestination = useMemo(() => {
    switch (classificationDestinationConf?.type) {
      case Type.Folder:
        // here we must manually recompose the type because the library used to automatically generate the type from the JSON schema consumed by RJSF is not able to generate conditional types (if/else...)
        const compositeType = classificationDestinationConf as ClassificationDestinationConf &
          ImportClassificationDestinationFolderOption;
        return {
          type: ClassificationDestinationType.FOLDER,
          options: compositeType.options as ClassificationDestinationOptionsFolder,
        };
      default:
        return {
          type: ClassificationDestinationType.CLASSIFICATION_VALIDATION_REQUEST,
        };
    }
  }, [classificationDestinationConf]);
  if (!fields) {
    return null;
  }

  const overrideClassificationStep =
    getCustomerOverrideFunction<FunctionOverrideKey.OverrideClassificationStep>(
      FunctionOverrideKey.OverrideClassificationStep,
      {},
    );

  /**
   * warning: it is essential not to "render" the component below without reason => otherwise, during the upload or the indexing, there is a risk of triggering several processes in parallel.
   * Please pay attention to injected props (use memo or callback if needed)
   */
  return (
    <KFImport
      internalFieldNames={internalFieldNames}
      formTypeFormIdMappings={formTypeFormIdMappings}
      userEmail={currentUser.email || ""}
      open={importModalOpen}
      uploadKey={UploadKey.IMPORT}
      onClose={handleClose}
      overrideClassificationStep={overrideClassificationStep}
      indexInAllAvailableLocale={
        // by default, we activate indexing in all languages => undefined or null
        indexInAllAvailableLocale === undefined ||
        indexInAllAvailableLocale === null ||
        indexInAllAvailableLocale
      }
      classificationDestination={classificationDestination}
      KIcon={KIcon}
    />
  );
};
export default ImportElement;
